// The door-side reader of the authentication verdict the app injects through
// `configureAgentsData` (ruling 65,
// `docs/monorepo-ui-floor-rulings/65-identity-crosses-by-injection.md`).
//
// Headless by construction: no design system, no router, no store, no `@/`.
// `useIsAuthenticated` has no package hook and no owning package — its verdict
// is provider-aware and store-coupled — so it crosses per consuming domain as a
// resolved boolean rather than opening an `@heidi/auth` read door.

import { getAgentsDataConfig, type AgentsDataConfig } from './config';

/**
 * Fails loudly and BY NAME. `getAgentsDataConfig` only knows whether the seam
 * was configured at all, so an un-injected member would otherwise reach a
 * consumer as `undefined` and blow up somewhere with no member in the message.
 *
 * Exported for the package's other readers of an optional member, such as
 * `./injected-identity`, so they all fail with this one message.
 * NOT on the `./data` door: `data/index.ts` names what this module publishes,
 * so a package-internal export stops here.
 */
export const injected = <K extends keyof AgentsDataConfig>(
  member: K
): NonNullable<AgentsDataConfig[K]> => {
  const value = getAgentsDataConfig()[member];
  if (value === undefined) {
    throw new Error(
      `[agents/data] \`${member}\` is not configured — the app injects it at ` +
        'src/utils/agents-data-wiring.ts (ruling 65); a test exercising this ' +
        'reader must configure a double for that member first.'
    );
  }
  return value as NonNullable<AgentsDataConfig[K]>;
};

/** The app's provider-aware authentication verdict. */
export const useIsAuthenticated = (): boolean => injected('useIsAuthenticated')();
